// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomOrderLineModel } from "@seacoastusa/App/Components/CustomeCartLineDetails/CustomCartLineDetails";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { css } from "styled-components";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@gcg/XDL/Helpers";
import { color } from "@gcg/XDL/Utilities";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import CustomCartLineDetails from "@seacoastusa/App/Components/CustomeCartLineDetails/CustomCartLineDetails";
interface OwnProps {
    orderLines: Array<CustomOrderLineModel>;
}

export interface OrderDetailSummaryTableStyles {
    firstTableHeader: DataTableHeaderProps;
    tableHeader: DataTableHeaderProps;
    tableWarpper: InjectableCss;
    infoLabel: TypographyPresentationProps;
    tableHeadStyle: DataTableHeadProps;
    tableBodyStyle: DataTableBodyProps;
}

export const orderDetailsSummaryTable: OrderDetailSummaryTableStyles = {
    tableWarpper: {
        css: css`
            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    firstTableHeader: {
        typographyProps: {
            size: 14,
            weight: 500,
        },
        css: css`
            width: 60%;
            height: 30px;
            border-top: 2px solid #002169;
        `,
    },
    tableHeader: {
        typographyProps: {
            size: 14,
            weight: 500,
        },
        css: css`
            width: calc(40% / 2);
            height: 30px;
            border-top: 2px solid #002169;
        `,
    },
    infoLabel: {
        forwardAs: "div",
        size: 20,
        weight: 500,
        css: css`
            margin-bottom: 20px;
        `,
    },
    tableHeadStyle: {
        css: css`
            height: 45px;
            &&&:focus,
            &&&:hover {
                background: transparent;
                cursor: unset;
            }
            ${screen("lte", "md")`
                 display: flex;
                 ${TypographyStyle} {
                     display: none;
                 }
                 @media print {
                     &&& {
                         -webkit-print-color-adjust: exact;
                     }   
                 }
             `}
        `,
    },
    tableBodyStyle: {
        css: css`
            ${screen("lte", "md")`
                 display: flex;
                 flex-direction: column;
             `}

            @media print {
                tr {
                    flex-direction: row;
                    border: 1px solid ${color("common.ui4")};
                }
            }
        `,
    },
};

type Props = OwnProps;
const styles = orderDetailsSummaryTable;

const OrderDetailsSummaryTableV2: React.FunctionComponent<Props> = ({ orderLines }) => {
    const headerColumns = [`${translate("Item Description")}`, `${translate("QTY")}`, ``];

    return (
        <StyledWrapper {...styles.tableWarpper}>
            <Typography {...styles.infoLabel}>{translate("Products")}</Typography>
            <DataTable>
                <DataTableHead {...styles.tableHeadStyle}>
                    {headerColumns.map((column, index) => (
                        <DataTableHeader
                            //  eslint-disable-next-line react/no-array-index-key
                            key={`${column}_${index}`}
                            {...(index === 0 ? styles.firstTableHeader : styles.tableHeader)}
                        >
                            {column}
                        </DataTableHeader>
                    ))}
                </DataTableHead>
                <DataTableBody {...styles.tableBodyStyle}>
                    {orderLines?.map(orderLine => (
                        <CustomCartLineDetails
                            key={orderLine.itemNumber}
                            headerColumns={headerColumns}
                            orderLine={orderLine}
                        />
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default OrderDetailsSummaryTableV2;
